import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { resetModel } from '../../store/components/modelSlice';
import Table from '../../components/table';
import { getChargeRecord , getOrderRecord } from '../../services/dashboard/charge';
import { getMemberChargeRecord } from '../../services/dashboard/member';

// ==========================================================
// 輔助組件: 報表行 (Label 為金色，Value 統一為黑色)
// ==========================================================
const InfoRow = ({ label, value }) => (
    <div className="report-row">
        <div className="report-label">{label}</div>
        <div className="report-value">{value ?? '--'}</div>
    </div>
);

// ==========================================================
// 數據格式化邏輯 (保留 Key 值對應)
// ==========================================================
const formatContentDetails = (content) => {
    if (!content) return {};
    const isPaymentSkipped = ([0, 2, 5].includes(content.tradeResult) || (content.tradeResult === -10 && content.tradeAmount === 0));
    const getSafeValue = (val) => isPaymentSkipped ? '--' : val;

    return {
        ...content,
        priceDisplay: content.price ? `NT$${content.price}` : '--',
        tradeAmountDisplay: content.tradeAmount ? `NT$${content.tradeAmount}` : '--',
        debtDisplay: `NT$${content.debt ?? 0}`,
        chargingDiscountDisplay: content.chargingDiscount ? `NT$${content.chargingDiscount}` : '--',
        invoiceNoDisplay: getSafeValue(content.invoiceNo),
        salesAmountDisplay: content.salesAmount ? `NT$${content.salesAmount}` : '--',
        bonusPercentDisplay: content.bonusPercent ? `${content.bonusPercent}%` : '--',
    };
};

const ChargeModel = () => {
    const dispatch = useDispatch();
    const { type, content, ikeyUid } = useSelector((state) => state.model.data);
    const [source, setSource] = useState(null);
    const [showExtraModal, setShowExtraModal] = useState(false);
    const [extraDataContent, setExtraDataContent] = useState('');

    const f = formatContentDetails(content); 
    const cancelHandler = () => { dispatch(resetModel()); };

    const handleShowExtraModal = (data) => {
        try {
            const displayData = typeof data === 'string' ? JSON.stringify(JSON.parse(data), null, 2) : JSON.stringify(data, null, 2);
            setExtraDataContent(displayData);
        } catch (e) { setExtraDataContent(data || '無資料'); }
        setShowExtraModal(true);
    };

    useEffect(() => {
        if (!type || !content) return;
        const apiMap = {
            report: { func: getChargeRecord, params: [content.uid] },
            order: { func: getOrderRecord, params: [content.uid] },
            tabCharge: { func: getMemberChargeRecord, params: [ikeyUid, content.uid] }
        };
        const selectedApi = apiMap[type];
        if (selectedApi) {
            selectedApi.func(...selectedApi.params)
                .then((res) => setSource(res?.data))
                .catch((err) => console.error(err));
        }
    }, [type, content, ikeyUid]);

    return (
        <>
            <Modal className='charge-report-v2' size='xl' show={true} onHide={cancelHandler} centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontWeight: 'bold' }}>訂單詳情回報</Modal.Title>
                </Modal.Header>
                <Modal.Body className="report-modal-body">
                    {/* 一、 會員與訂單基本資訊 */}
                    <div className="section-container">
                        <div className="main-title">一、會員與訂單基本資訊</div>
                        <div className="flex-grid">
                            <div className="grid-box">
                                <div className="box-title">會員資訊</div>
                                <InfoRow label="會員帳號" value={f.account} />
                                <InfoRow label="姓名" value={f.name} />
                                <InfoRow label="車身碼 (VIN)" value={f.vin} />
                                <InfoRow label="車牌" value={f.carNo} />
                                <InfoRow label="EVCCID" value={f.evccid} />
                            </div>
                            <div className="grid-box">
                                <div className="box-title">訂單資訊</div>
                                <InfoRow label="訂單編號" value={f.uid} />
                                <InfoRow label="訂單建立時間" value={f.createTimeString} />
                                <InfoRow label="訂單建立狀態" value={f.orderCreateStatus ?? '建立成功'} />
                                <div className="box-title border-top">充電資訊</div>
                                <InfoRow label="RequestID" value={f.requestId} />
                                <InfoRow label="充電金額" value={f.priceDisplay} />
                                <InfoRow label="充電度數" value={`${f.kwh ?? 0} kWh`} />
                                <InfoRow label="充電開始時間" value={f.startTimeString} />
                                <InfoRow label="充電結束時間" value={f.endTimeString} />
                                <InfoRow label="充電業者 (CPO)" value={f.cpoName} />
                                <InfoRow label="充電站名稱" value={f.locationName} />
                                <InfoRow label="充電站代碼" value={f.locationId} />
                                <InfoRow label="充電樁代碼" value={f.evseId} />
                                <InfoRow label="充電槍代碼" value={f.connectorId} />
                            </div>
                        </div>
                    </div>

                    {/* 二、 訂單摘要 */}
                    <div className="section-container mt-4">
                        <div className="main-title">二、訂單摘要</div>
                        <div className="summary-border-box">
                            <div className="sub-section">
                                <div className="sub-section-title">支付摘要子區塊</div>
                                <InfoRow label="帳單來源" value={f.payVendorString} />
                                <InfoRow label="支付結果" value={f.tradeResultString} />
                                <InfoRow label="支付狀態" value={f.billStatusString} />
                                <InfoRow label="支付卡號末四碼" value={f.truncatedCardNo} />
                                <InfoRow label="實刷金額" value={f.tradeAmountDisplay} />
                                <InfoRow label="欠費金額" value={f.debtDisplay} />
                                <InfoRow label="授權成功時間" value={f.payTimeString ?? f.repayDateString} />
                                <InfoRow label="銀行訂單編號" value={f.transactionNo ?? f.repayTransactionNo} />
                                <InfoRow label="退款時間" value={f.refundTimeString} />
                                <InfoRow label="退款人員" value={f.refundUser} />
                            </div>

                            <div className="sub-section mt-3">
                                <div className="sub-section-title">全單合約摘要子區塊</div>
                                <InfoRow label="全單折抵合約編號" value={f.chargingDiscountContractId} />
                                <InfoRow label="全單折抵專案名稱" value={f.chargingDiscountName} />
                                <InfoRow label="全單折抵金額" value={f.chargingDiscountDisplay} />
                            </div>

                            <div className="sub-section mt-3">
                                <div className="sub-section-title">點數摘要子區塊</div>
                                <InfoRow label="點數折抵狀態" value={f.bonusResultString} />
                                <InfoRow label="折抵點數" value={f.bonusDiscount} />
                                <InfoRow label="點數回饋狀態" value={f.bonusAddResultString} />
                                <InfoRow label="點數回饋比例" value={f.bonusPercentDisplay} />
                                <InfoRow label="回饋點數" value={f.bonusAdd} />
                                <InfoRow label="點數回饋時間" value={f.bonusAddDateString} />
                                <InfoRow label="折抵點數歸還人員" value={f.bonusReturnUser} />
                                <InfoRow label="折抵點數歸還時間" value={f.bonusReturnTimeString} />
                                <InfoRow label="贈予點數收回人員" value={f.bonusSurrenderUser} />
                                <InfoRow label="贈予點數收回時間" value={f.bonusSurrenderTimeString} />
                            </div>

                            <div className="sub-section mt-3">
                                <div className="sub-section-title">發票摘要子區塊</div>
                                <InfoRow label="發票狀態" value={f.invoiceResultString} />
                                <InfoRow label="發票號碼" value={f.invoiceNoDisplay} />
                                <InfoRow label="發票開立時間" value={f.invoiceDateString} />
                                <InfoRow label="發票金額" value={f.salesAmountDisplay} />
                                <InfoRow label="開立方式" value={f.invoiceTypeString} />
                                <InfoRow label="會員載具" value={f.email} />
                                <InfoRow label="手機載具" value={f.mobileCarrier} />
                                <InfoRow label="公司編號" value={f.customerIdentifier} />
                                <InfoRow label="公司抬頭" value={f.customerName} />
                                <InfoRow label="作廢/折讓人員" value={f.invalidUser} />
                                <InfoRow label="作廢/折讓時間" value={f.invalidTimeString} />
                            </div>
                        </div>
                    </div>

                    {/* 三、 訂單歷程明細 */}
                    <div className="section-container mt-4 pb-4">
                        <div className="main-title">三、訂單歷程明細</div>
                        <div className="table-wrapper">
                            <Table 
                                header={['時間戳記', '事件名稱', '結果', '來源系統', '摘要資訊', '詳細資訊']} 
                                body={() => (
                                    <tbody>
                                        {source?.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="text-center">{item.createTimeString}</td>
                                                <td>{item.actionName}</td>
                                                <td className="text-center">{item.executeStatus ? '成功' : <span className="text-danger">失敗</span>}</td>
                                                <td>{item.sourceName}</td>
                                                <td className="info-cell">{item.message}</td>
                                                <td className="text-center">
                                                    {item.extraData && (
                                                        <Button size="sm" variant="outline-dark" onClick={() => handleShowExtraModal(item.extraData)}>
                                                            顯示
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                )} 
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancelHandler}>關閉</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showExtraModal} onHide={() => setShowExtraModal(false)} centered size="lg">
                <Modal.Header closeButton><Modal.Title>詳細資訊 (JSON)</Modal.Title></Modal.Header>
                <Modal.Body>
                    <pre className="json-block">{extraDataContent}</pre>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ChargeModel;